import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Badge,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import RemoveIcon from "@mui/icons-material/Remove";
import axios from "axios";
import config from "../config.js";
import { useSelector } from "react-redux";

const BoxGroup = ({ isDrawerOpen, handleDrawerToggle }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const userId = useSelector((state) => state.auth.user?.id);
  const token = useSelector((state) => state.auth.token);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const { API_BASE_URL } = config;
  const theme = useTheme();

  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const fetchUserFiles = useCallback(async () => {
    try {
      setLoading(true);

      const authToken = token || localStorage.getItem("token");

      const response = await axios.get(
        `${API_BASE_URL}/api/v1/files/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const filesData = response.data.data?.files || [];

      setFiles(filesData);
    } catch (error) {
      // console.error("BoxGroup: Error fetching files:", error);
      setSnackbar({
        open: true,
        message: "Failed to load files",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [userId, token,API_BASE_URL]);
  useEffect(() => {
    if (userId) {
      fetchUserFiles();
    }
  }, [userId,fetchUserFiles]);

  const handleDownloadClick = (event) => {
    fetchUserFiles();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const excelFiles = files.filter((file) => {
    if (!file || !file.fileName) return false;
    const lowerFileName = file.fileName.toLowerCase();
    return lowerFileName.endsWith(".xlsx") || lowerFileName.endsWith(".xls");
  });

  const downloadFile = async (fileId, fileName) => {
    try {
      handleCloseMenu();

      setSnackbar({
        open: true,
        message: `Downloading ${fileName}...`,
        severity: "info",
      });

      // Get authentication token from localStorage as fallback
      const authToken = token || localStorage.getItem("token");

      // Use the download/:fileId endpoint as specified in the requirements
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/download/${fileId}`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      setSnackbar({
        open: true,
        message: "File downloaded successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Download error:", error);
      setSnackbar({
        open: true,
        message: `Failed to download file: ${error.message}`,
        severity: "error",
      });
    }
  };

  const downloadBalanceSheet = async () => {
    try {
      setSnackbar({
        open: true,
        message: "Preparing balance sheet download...",
        severity: "info",
      });

      const authToken = token || localStorage.getItem("token");

      const response = await axios.get(
        `${API_BASE_URL}/api/v1/current-balance-sheet`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "current_balance_sheet.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      setSnackbar({
        open: true,
        message: "Current balance sheet downloaded successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Balance sheet download error:", error);
      setSnackbar({
        open: true,
        message: `Failed to download balance sheet: ${error.message}`,
        severity: "error",
      });
    }
  };

  const getResponsiveStyles = () => ({
    container: {
      position: "fixed",
      top: 0,
      right: 0,
      m: isMobile ? 2 : isTablet ? 3 : 5,
      zIndex: 1300,
    },
    buttonGroup: {
      marginTop: isDrawerOpen
        ? isMobile
          ? -1
          : -2
        : isMobile
        ? 3
        : isTablet
        ? 4
        : 5,
      marginLeft: isDrawerOpen
        ? isMobile
          ? 5
          : isTablet
          ? 8
          : 10
        : isMobile
        ? 3
        : isTablet
        ? 5
        : 7,
      justifyItems: "center",
      backgroundColor: theme.palette.primary.main,
      flexDirection: isMobile ? "column" : "row",
      "& .MuiButton-root": {
        minWidth: isMobile ? "40px" : isTablet ? "45px" : "50px",
        padding: isMobile ? "8px" : isTablet ? "10px" : "12px",
      },
    },
    button: {
      backgroundColor: theme.palette.primary.main,
      display: "flex",
      justifyItems: "center",
      alignItems: "center",
      "&:hover": {
        backgroundColor: theme.palette.primary.dark || "#FF4D4D",
      },
      fontSize: isMobile ? "0.8rem" : "1rem",
    },
    badge: {
      "& .MuiBadge-badge": {
        backgroundColor: "#fff",
        color: theme.palette.primary.main,
        fontSize: isMobile ? "0.7rem" : "0.75rem",
        minWidth: isMobile ? "16px" : "20px",
        height: isMobile ? "16px" : "20px",
      },
    },
    menu: {
      maxHeight: isMobile ? 250 : 300,
      width: isMobile ? "200px" : isTablet ? "225px" : "250px",
    },
    snackbar: {
      bottom: isMobile ? 16 : 24,
      right: isMobile ? 16 : 24,
    },
  });

  const styles = getResponsiveStyles();

  return (
    <div>
      <Box sx={styles.container}>
        <ButtonGroup
          sx={styles.buttonGroup}
          variant="contained"
          aria-label="Basic button group"
          orientation={isMobile ? "vertical" : "horizontal"}
        >
          <Button sx={styles.button}>
            <RemoveIcon fontSize={isMobile ? "small" : "medium"} />
          </Button>
          <Button sx={styles.button}>
            <AddIcon fontSize={isMobile ? "small" : "medium"} />
          </Button>
          <Button sx={styles.button} onClick={handleDownloadClick}>
            <Badge
              badgeContent={loading ? "..." : excelFiles.length}
              color="primary"
              sx={styles.badge}
            >
              <ArrowCircleDownIcon fontSize={isMobile ? "small" : "medium"} />
            </Badge>
          </Button>
          <Button sx={styles.button} onClick={handleDrawerToggle}>
            <RemoveRedEyeIcon fontSize={isMobile ? "small" : "medium"} />
          </Button>
        </ButtonGroup>
      </Box>

      {/* Download Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          style: styles.menu,
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={downloadBalanceSheet}>
          <strong style={{ fontSize: isMobile ? "0.9rem" : "1rem" }}>
            Current Balance Sheet
          </strong>
        </MenuItem>

        {loading && (
          <MenuItem>
            <CircularProgress size={isMobile ? 16 : 20} sx={{ mr: 1 }} />
            <span style={{ fontSize: isMobile ? "0.9rem" : "1rem" }}>
              Loading files...
            </span>
          </MenuItem>
        )}

        {!loading && excelFiles.length > 0 && (
          <>
            <MenuItem disabled divider>
              <em style={{ fontSize: isMobile ? "0.8rem" : "0.9rem" }}>
                Generated Files
              </em>
            </MenuItem>

            {excelFiles.map((file) => (
              <MenuItem
                key={file.fileId}
                onClick={() => downloadFile(file.fileId, file.fileName)}
                sx={{
                  fontSize: isMobile ? "0.85rem" : "0.95rem",
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                  maxWidth: "100%",
                }}
              >
                {file.fileName}
              </MenuItem>
            ))}
          </>
        )}

        {!loading && excelFiles.length === 0 && (
          <MenuItem disabled>
            <em style={{ fontSize: isMobile ? "0.8rem" : "0.9rem" }}>
              No generated files yet
            </em>
          </MenuItem>
        )}
      </Menu>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: isMobile ? "center" : "right",
        }}
        sx={{
          bottom: styles.snackbar.bottom,
          right: isMobile ? "auto" : styles.snackbar.right,
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            fontSize: isMobile ? "0.85rem" : "0.9rem",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default BoxGroup;
